import type { Editor } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { useEditorState } from '@tiptap/react';
import { PanelBottomCloseIcon, PanelLeftCloseIcon, PanelRightCloseIcon, PanelTopCloseIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import type { DropdownOption } from '@/components/ui/tiptap/tiptap-ui-primitives/dropdown-menu';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

import type { AddColRowButtonProps, AddTableElementType } from '.';

export const addColRowOptions: DropdownOption<AddTableElementType>[] = [
  {
    label: 'Dodaj kolumnę przed',
    type: 'addColumnBefore',
    icon: PanelLeftCloseIcon,
  },
  {
    label: 'Dodaj kolumnę po',
    type: 'addColumnAfter',
    icon: PanelRightCloseIcon,
  },
  {
    label: 'Dodaj wiersz przed',
    type: 'addRowBefore',
    icon: PanelTopCloseIcon,
  },

  {
    label: 'Dodaj wiersz po',
    type: 'addRowAfter',
    icon: PanelBottomCloseIcon,
  },
];

function clickAddColRowItem(editor: Editor | null, type: AddTableElementType): void {
  if (!editor) return;
  switch (type) {
    case 'addColumnBefore':
      editor.chain().focus().addColumnBefore().run();
      break;
    case 'addRowBefore':
      editor.chain().focus().addRowBefore().run();
      break;
    case 'addColumnAfter':
      editor.chain().focus().addColumnAfter().run();
      break;
    case 'addRowAfter':
      editor.chain().focus().addRowAfter().run();
      break;
  }
}

function useIsActiveElement(editor: Editor | null, type: AddTableElementType): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        return ctx.editor?.isActive(type) ?? false;
      },
    }) ?? false
  );
}

function getOption(type: AddTableElementType): DropdownOption<AddTableElementType> | undefined {
  return addColRowOptions.find((option) => option.type === type);
}

function useAddColRowState(editor: Editor | null, type: AddTableElementType) {
  const isActive: boolean = useIsActiveElement(editor, type);
  const option: DropdownOption<AddTableElementType> | undefined = getOption(type);

  return {
    isActive,
    option,
  };
}

function useHandleClick(onClick: AddColRowButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function AddColRowButton(addColRowButtonProps: AddColRowButtonProps) {
  const { editor, type, onClick, className, children, ...props } = addColRowButtonProps;
  const { isActive, option } = useAddColRowState(editor, type);
  const inSchema: boolean = isNodeInSchema('table', editor);

  const Icon = option?.icon || PanelTopCloseIcon;

  const handleClick = useHandleClick(onClick, () => clickAddColRowItem(editor, type));

  const show: boolean = useMemo(() => {
    return shouldShow([!!editor, inSchema]);
  }, [editor, inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      aria-label={option?.label || type}
      aria-pressed={isActive}
      onClick={handleClick}
      className={cn('group aria-pressed:text-text-primary aria-pressed:bg-fill-secondary/60 aria-pressed:hover:bg-fill-secondary/80', className)}
      {...props}
    >
      {children || (
        <>
          <Icon className="group:aria-pressed:text-text-primary" />
          {option?.label || type}
        </>
      )}
    </DropdownMenuItem>
  );
}
